import { useRef, useEffect, useState, useMemo, useId, useLayoutEffect } from 'react';

const CurvedLoop = ({
    marqueeText = '',
    speed = 2,
    className,
    containerClass = '',
    curveAmount = 400,
    direction = 'left',
    interactive = true
}) => {
    const text = useMemo(() => {
        const hasTrailing = /\s|\u00A0$/.test(marqueeText);
        return (hasTrailing ? marqueeText.replace(/\s+$/, '') : marqueeText) + '\u00A0';
    }, [marqueeText]);

    const measureRef = useRef(null);
    const textPathRef = useRef(null);
    const pathRef = useRef(null);
    const [spacing, setSpacing] = useState(0);
    const offsetRef = useRef(0);
    const uid = useId();
    const pathId = `curve-${uid}`;
    const pathD = `M-100,40 Q2500,${40 + curveAmount} 4540,40`;

    const dragRef = useRef(false);
    const lastXRef = useRef(0);
    const dirRef = useRef(direction);
    const velRef = useRef(0);

    const textLength = spacing;
    const totalText = textLength
        ? Array(Math.ceil(4800 / textLength) + 2)
            .fill(text)
            .join('')
        : text;
    const ready = spacing > 0;

    function wrapOffset(value, period) {
        if (period <= 0) return value;
        let o = value;
        while (o <= -period) o += period;
        while (o > 0) o -= period;
        return o;
    }

    useEffect(() => {
        if (measureRef.current) setSpacing(measureRef.current.getComputedTextLength());
    }, [text, className]);

    useLayoutEffect(() => {
        if (!spacing || !textPathRef.current) return;
        const initial = -spacing;
        offsetRef.current = initial;
        textPathRef.current.setAttribute('startOffset', `${initial}px`);
    }, [spacing]);

    useEffect(() => {
        if (!spacing) return;
        let frame = 0;
        const step = () => {
            if (!dragRef.current && textPathRef.current) {
                const delta = dirRef.current === 'right' ? speed : -speed;
                const newOffset = wrapOffset(offsetRef.current + delta, spacing);
                offsetRef.current = newOffset;
                textPathRef.current.setAttribute('startOffset', `${newOffset}px`);
            }
            frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [spacing, speed]);

    const onPointerDown = e => {
        if (!interactive) return;
        dragRef.current = true;
        lastXRef.current = e.clientX;
        velRef.current = 0;
        e.target.setPointerCapture(e.pointerId);
    };

    const onPointerMove = e => {
        if (!interactive || !spacing || !dragRef.current || !textPathRef.current) return;
        const dx = e.clientX - lastXRef.current;
        lastXRef.current = e.clientX;
        velRef.current = dx;
        const newOffset = wrapOffset(offsetRef.current + dx, spacing);
        offsetRef.current = newOffset;
        textPathRef.current.setAttribute('startOffset', `${newOffset}px`);
    };

    const endDrag = () => {
        if (!interactive) return;
        dragRef.current = false;
        dirRef.current = velRef.current > 0 ? 'right' : 'left';
    };

    const cursorStyle = interactive ? (dragRef.current ? 'grabbing' : 'grab') : 'auto';

    const svgBackgroundStyle = {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        background:
            'linear-gradient(90deg, rgba(156, 78, 23, 1) 0%, rgba(254, 144, 67, 1) 100%, rgba(249, 123, 34, 1) 51%)',
    };

    return (
        <div
            className={`w-full h-full flex items-center justify-center overflow-hidden ${containerClass}`}
            style={{
                visibility: ready ? 'visible' : 'hidden',
                cursor: cursorStyle,
                background: 'unset',
                backgroundColor: 'unset',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
        >
            <svg
                className="select-none w-full h-full overflow-visible block text-[2.5rem] font-bold uppercase leading-none"
                viewBox="0 0 4440 80"
                preserveAspectRatio="xMidYMid slice"
                style={svgBackgroundStyle}
            >
                <text
                    ref={measureRef}
                    xmlSpace="preserve"
                    style={{
                        visibility: 'hidden',
                        opacity: 0,
                        pointerEvents: 'none',
                        background: 'unset',
                        backgroundColor: 'unset',
                    }}
                >
                    {text}
                </text>
                <defs style={{ background: 'unset', backgroundColor: 'unset' }}>
                    <path ref={pathRef} id={pathId} d={pathD} fill="none" stroke="transparent" />
                </defs>
                {ready && (
                    <text
                        xmlSpace="preserve"
                        className={`fill-white ${className ?? ''}`}
                        dominantBaseline="middle"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 1)' }}
                    >
                        <textPath ref={textPathRef} href={`#${pathId}`} xmlSpace="preserve">
                            {totalText}
                        </textPath>
                    </text>
                )}
            </svg>
        </div>
    );
};

export default CurvedLoop;
